import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { analysisAPI, periodsAPI, teamsAPI, kpisAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Target,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Clock,
  Calendar,
  CalendarDays,
  ArrowRight,
  CheckCircle2,
  XCircle,
  X,
  Filter,
  Building2,
  UsersRound,
  Eye,
  Hash,
} from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';
import { 
  formatDate, 
  getBimesterName, 
  formatNumber, 
  cn,
  STATUS_CONFIG,
  getStatusFilterBadgeClass,
  getPeriodicityLabel,
  formatGoalRange,
} from '../lib/utils';
import { useKPIOrder } from '../lib/useKPIOrder';

// ============================================
// CONSTANTS
// ============================================
const STATUS_ICON_MAP = {
  success: CheckCircle2,
  error: XCircle,
  pending: Clock,
};

// ============================================
// HELPER COMPONENTS
// ============================================

/**
 * Status badge component - uses centralized config
 */
const StatusBadge = ({ status, isRange = false }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const label = isRange && status === 'success' ? 'En rango'
    : isRange && status === 'error' ? 'Fuera de rango'
    : config.label;
  return (
    <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium border', config.badgeClass)}>
      {label}
    </span>
  );
};

/**
 * Status icon component - uses centralized config
 */
const StatusIcon = ({ status }) => {
  const Icon = STATUS_ICON_MAP[status] || Clock;
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return <Icon className={cn('w-5 h-5', config.iconColor)} />;
};

/**
 * KPI Row component for Dashboard list
 */
const KPIRow = ({ kpi, onNavigate }) => {
  const isWeekly = !kpi.periodicity || kpi.periodicity === 'weekly';

  return (
    <div
      className="p-3 sm:p-4 hover:bg-slate-50 transition-colors cursor-pointer"
      onClick={() => onNavigate(`/analysis/${kpi.id}`)}
      data-testid={`kpi-row-${kpi.id}`}
    >
      {/* Mobile Layout */}
      <div className="flex flex-col gap-3 sm:hidden">
        <div className="flex items-center gap-3">
          <StatusIcon status={kpi.status} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-slate-900 truncate uppercase">{kpi.name}</p>
              {!isWeekly && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-blue-50 text-blue-700 border-blue-200 flex-shrink-0">
                  <CalendarDays className="w-2.5 h-2.5 mr-0.5" />
                  {getPeriodicityLabel(kpi.periodicity)}
                </Badge>
              )}
              {kpi.goal_calculation_type === 'average' && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-violet-50 text-violet-700 border-violet-200 flex-shrink-0">
                  Prom
                </Badge>
              )}
              {kpi.goal_calculation_type === 'fixed_per_period' && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-amber-50 text-amber-700 border-amber-200 flex-shrink-0">
                  Límite
                </Badge>
              )}
              {kpi.goal_calculation_type === 'range' && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-emerald-50 text-emerald-700 border-emerald-200 flex-shrink-0">
                  Rango
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              {kpi.responsible_name && (
                <>
                  <UserAvatar name={kpi.responsible_name} avatarUrl={kpi.responsible_avatar} size="xs" />
                  <span className="text-xs text-slate-500 truncate">{kpi.responsible_name}</span>
                </>
              )}
              {!kpi.responsible_name && <p className="text-xs text-slate-500 uppercase">{kpi.area}</p>}
            </div>
          </div>
          <StatusBadge status={kpi.status} isRange={kpi.goal_calculation_type === 'range'} />
        </div>
        <div className="flex items-center justify-between text-sm pl-8">
          <div>
            <span className="text-slate-500">
              {kpi.goal_calculation_type === 'range' ? 'Resultado:' : (kpi.goal_calculation_type === 'average' || kpi.goal_calculation_type === 'fixed_per_period' ? 'Promedio:' : 'Resultado:')}
            </span>{' '}
            <span className="font-medium">
              {kpi.goal_calculation_type === 'range'
                ? `${kpi.captured_periods > 0 ? formatNumber(kpi.total_result) : '-'} (rango: ${formatGoalRange(kpi.goal_range)})`
                : `${formatNumber(kpi.total_result)} / ${formatNumber(kpi.total_goal)}`}
            </span>
          </div>
          <div>
            <span className="font-semibold">
              {kpi.goal_calculation_type === 'range'
                ? '-'
                : (typeof kpi.performance === 'number' ? `${kpi.performance.toFixed(1)}%` : '-')}
            </span>
          </div>
        </div>
      </div>
      
      {/* Desktop Layout */}
      <div className="hidden sm:flex items-center justify-between">
        <div className="flex items-center gap-4">
          <StatusIcon status={kpi.status} />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-slate-900 uppercase">{kpi.name}</p>
              {!isWeekly && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 border-blue-200">
                  <CalendarDays className="w-3 h-3 mr-1" />
                  {getPeriodicityLabel(kpi.periodicity)}
                </Badge>
              )}
              {kpi.goal_calculation_type === 'average' && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-violet-50 text-violet-700 border-violet-200">
                  Promedio
                </Badge>
              )}
              {kpi.goal_calculation_type === 'fixed_per_period' && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 border-amber-200">
                  Límite fijo
                </Badge>
              )}
              {kpi.goal_calculation_type === 'range' && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">
                  Rango
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {kpi.responsible_name && (
                <>
                  <UserAvatar name={kpi.responsible_name} avatarUrl={kpi.responsible_avatar} size="xs" />
                  <span className="text-sm text-slate-500">{kpi.responsible_name}</span>
                  <span className="text-slate-300">|</span>
                </>
              )}
              <p className="text-sm text-slate-500 uppercase">{kpi.area}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-sm text-slate-500">
              {kpi.goal_calculation_type === 'range' ? 'Resultado (rango)' : (kpi.goal_calculation_type === 'average' || kpi.goal_calculation_type === 'fixed_per_period' ? 'Promedio / Límite' : 'Resultado / Meta')}
            </p>
            <p className="text-lg font-semibold text-slate-900">
              {kpi.goal_calculation_type === 'range'
                ? `${kpi.captured_periods > 0 ? formatNumber(kpi.total_result) : '-'} (${formatGoalRange(kpi.goal_range)})`
                : `${formatNumber(kpi.total_result)} / ${formatNumber(kpi.total_goal)}`}
            </p>
          </div>
          <div className="text-right w-20">
            <p className="text-sm text-slate-500">Avance</p>
            <p className="text-lg font-semibold text-slate-900">
              {kpi.goal_calculation_type === 'range'
                ? '-'
                : (typeof kpi.performance === 'number' ? `${kpi.performance.toFixed(1)}%` : '-')}
            </p>
          </div>
          <StatusBadge status={kpi.status} isRange={kpi.goal_calculation_type === 'range'} />
          <ArrowRight className="w-5 h-5 text-slate-400" />
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin, isViewer } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedBimester, setSelectedBimester] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null); // null = aggregate all periods
  
  // Scope state for admins - 'all', 'my_teams', or team_id
  const [scope, setScope] = useState('all');
  const [teams, setTeams] = useState([]);
  
  // Filter state - initialized from URL params
  const [statusFilter, setStatusFilter] = useState(() => {
    return searchParams.get('status') || null;
  });

  // Fetch teams for admin scope selector
  useEffect(() => {
    if (isAdmin()) {
      teamsAPI.getAll()
        .then(res => setTeams(res.data.filter(t => t.is_active)))
        .catch(err => console.error('Error fetching teams:', err));
    }
  }, []);

  // Fetch periods when year changes
  useEffect(() => {
    periodsAPI.getAll({ year: selectedYear })
      .then(res => {
        setPeriods(res.data);
        // Reset period selection when year changes
        setSelectedPeriod(null);
      })
      .catch(err => console.error('Error fetching periods:', err));
  }, [selectedYear]);

  // Fetch current period on mount
  useEffect(() => {
    periodsAPI.getCurrent()
      .then(res => {
        setCurrentPeriod(res.data);
        // Set default selected period to current
        if (!selectedPeriod) {
          setSelectedPeriod(res.data.id);
        }
      })
      .catch(err => console.error('Error fetching current period:', err));
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedBimester, selectedPeriod, scope]);

  // Sync filter to URL
  useEffect(() => {
    if (statusFilter) {
      searchParams.set('status', statusFilter);
    } else {
      searchParams.delete('status');
    }
    setSearchParams(searchParams, { replace: true });
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { year: selectedYear };
      
      // If specific period selected, use period_id; otherwise use bimester
      if (selectedPeriod && selectedPeriod !== 'all') {
        params.period_id = selectedPeriod;
      } else if (selectedBimester) {
        params.bimester = selectedBimester;
      }
      
      // Add scope for admins
      if (isAdmin() && scope !== 'all') {
        params.scope = scope;
      }
      const [dashboardRes, periodRes] = await Promise.all([
        analysisAPI.getDashboard(params),
        periodsAPI.getCurrent(),
      ]);
      setDashboardData(dashboardRes.data);
      setCurrentPeriod(periodRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Use KPI ordering hook - always respect saved order from KPIs page
  const {
    orderedKpis,
    loading: orderLoading,
  } = useKPIOrder(dashboardData?.kpis || []);

  // Filter KPIs based on status - always use orderedKpis (single source of truth from KPI view)
  const getFilteredKpis = useCallback(() => {
    if (!orderedKpis.length) return [];
    if (!statusFilter) return orderedKpis;
    return orderedKpis.filter(kpi => kpi.status === statusFilter);
  }, [orderedKpis, statusFilter]);

  // Handle filter click on summary cards
  const handleFilterClick = (status) => {
    setStatusFilter(statusFilter === status ? null : status);
  };

  // Clear filter
  const clearFilter = () => {
    setStatusFilter(null);
  };

  // Get filter label - uses centralized config
  const getFilterLabel = (status) => {
    return STATUS_CONFIG[status]?.label || status;
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  const summary = dashboardData?.summary || { total: 0, success: 0, error: 0, pending: 0 };

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
        {/* Viewer Mode Banner */}
        {isViewer() && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <div className="flex items-center gap-2 text-amber-800">
              <Eye className="w-5 h-5" />
              <span className="font-medium">Modo Solo Lectura</span>
              <Badge variant="outline" className="ml-auto text-xs bg-amber-100 text-amber-700 border-amber-300">
                Viewer
              </Badge>
            </div>
            <p className="text-sm text-amber-700 mt-1">
              Solo puedes visualizar los KPIs asignados. No tienes permisos para modificar datos.
            </p>
          </div>
        )}
        
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">Dashboard</h1>
              {isViewer() && (
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                  <Eye className="w-3 h-3 mr-1" />
                  Viewer
                </Badge>
              )}
            </div>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Bienvenido, {user?.name}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-24 sm:w-28" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2026">2026</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Period Selector */}
            <Select 
              value={selectedPeriod || 'all'} 
              onValueChange={(v) => {
                setSelectedPeriod(v === 'all' ? null : v);
                // Clear bimester when selecting specific period
                if (v !== 'all') {
                  setSelectedBimester(null);
                }
              }}
            >
              <SelectTrigger className="w-32 sm:w-40" data-testid="period-select">
                <Hash className="w-4 h-4 mr-1 text-slate-400" />
                <SelectValue placeholder="Periodo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los PER</SelectItem>
                {periods.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <div className="flex items-center gap-2">
                      <span>PER {p.number}</span>
                      {currentPeriod?.id === p.id && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-emerald-50 text-emerald-700 border-emerald-200">
                          Actual
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Bimester Selector - Only show when no specific period selected */}
            {(!selectedPeriod || selectedPeriod === 'all') && (
              <Select value={selectedBimester?.toString() || 'all'} onValueChange={(v) => setSelectedBimester(v === 'all' ? null : parseInt(v))}>
                <SelectTrigger className="w-32 sm:w-40" data-testid="bimester-select">
                  <SelectValue placeholder="Bimestre" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {[1, 2, 3, 4, 5, 6].map((b) => (
                    <SelectItem key={b} value={b.toString()}>
                      {getBimesterName(b)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Scope selector for admins */}
            {isAdmin() && teams.length > 0 && (
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="w-40 sm:w-48" data-testid="scope-select">
                  <SelectValue placeholder="Alcance" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      <span>Toda la empresa</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="my_teams">
                    <div className="flex items-center gap-2">
                      <UsersRound className="w-4 h-4" />
                      <span>Mis equipos</span>
                    </div>
                  </SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id}>
                      <div className="flex items-center gap-2">
                        <UsersRound className="w-4 h-4" />
                        <span>{team.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Period Info Banner - Shows selected or current period */}
        {(() => {
          const displayPeriod = selectedPeriod && selectedPeriod !== 'all' 
            ? periods.find(p => p.id === selectedPeriod) 
            : currentPeriod;
          const isFiltered = selectedPeriod && selectedPeriod !== 'all' && selectedPeriod !== currentPeriod?.id;
          
          return displayPeriod && (
            <Card className={cn("border-0", isFiltered ? "bg-violet-900" : "bg-slate-900")} data-testid="current-period-banner">
              <CardContent className="py-3 sm:py-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className={cn(
                      "w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0",
                      isFiltered ? "bg-violet-500/20" : "bg-emerald-500/20"
                    )}>
                      {isFiltered ? (
                        <Hash className="w-5 h-5 sm:w-6 sm:h-6 text-violet-400" />
                      ) : (
                        <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className={cn("text-xs sm:text-sm", isFiltered ? "text-violet-300" : "text-slate-400")}>
                        {isFiltered ? 'Periodo Seleccionado' : 'Periodo Activo'}
                      </p>
                      <p className="text-lg sm:text-xl font-bold font-['Manrope'] truncate text-white">
                        PER {displayPeriod.number} - {getBimesterName(displayPeriod.bimester)}
                      </p>
                      <p className={cn("text-xs sm:text-sm", isFiltered ? "text-violet-300" : "text-slate-400")}>
                        {formatDate(displayPeriod.start_date)} - {formatDate(displayPeriod.end_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isFiltered && currentPeriod && (
                      <Button 
                        variant="secondary" 
                        onClick={() => setSelectedPeriod(currentPeriod.id)}
                        className="bg-white/10 hover:bg-white/20 text-white border-0"
                        size="sm"
                        data-testid="go-to-current"
                      >
                        <Calendar className="w-4 h-4 mr-1" />
                        Actual
                      </Button>
                    )}
                    <Button 
                      variant="secondary" 
                      onClick={() => navigate('/capture')}
                      className="bg-white/10 hover:bg-white/20 text-white border-0 w-full sm:w-auto"
                      data-testid="go-to-capture"
                    >
                      Ir a captura
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 w-full max-w-full">
          <Card 
            className={cn(
              "card-hover cursor-pointer transition-all overflow-hidden",
              !statusFilter && "ring-2 ring-slate-400 ring-offset-2"
            )}
            onClick={clearFilter}
            data-testid="total-kpis-card"
          >
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-slate-500 text-xs sm:text-sm font-medium truncate">Total KPIs</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope'] mt-1">{summary.total}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
                </div>
              </div>
              {!statusFilter && (
                <div className="mt-2 text-xs text-slate-500 font-medium flex items-center gap-1">
                  <Filter className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">Mostrando todos</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card 
            className={cn(
              "card-hover cursor-pointer transition-all overflow-hidden",
              statusFilter === 'success' && "ring-2 ring-emerald-500 ring-offset-2"
            )}
            onClick={() => handleFilterClick('success')}
            data-testid="success-kpis-card"
          >
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-slate-500 text-xs sm:text-sm font-medium truncate">En Meta</p>
                  <p className="text-2xl sm:text-3xl font-bold text-emerald-600 font-['Manrope'] mt-1">{summary.success}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                </div>
              </div>
              {statusFilter === 'success' && (
                <div className="mt-2 text-xs text-emerald-600 font-medium flex items-center gap-1">
                  <Filter className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">Filtro activo</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card 
            className={cn(
              "card-hover cursor-pointer transition-all overflow-hidden",
              statusFilter === 'error' && "ring-2 ring-rose-500 ring-offset-2"
            )}
            onClick={() => handleFilterClick('error')}
            data-testid="error-kpis-card"
          >
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-slate-500 text-xs sm:text-sm font-medium truncate">Fuera de Meta</p>
                  <p className="text-2xl sm:text-3xl font-bold text-rose-600 font-['Manrope'] mt-1">{summary.error}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-rose-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-rose-600" />
                </div>
              </div>
              {statusFilter === 'error' && (
                <div className="mt-2 text-xs text-rose-600 font-medium flex items-center gap-1">
                  <Filter className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">Filtro activo</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* KPIs List */}
        <Card data-testid="kpis-list-card">
          <CardHeader className="border-b border-slate-100 px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <CardTitle className="text-base sm:text-lg font-['Manrope']">
                Resumen de KPIs
                {statusFilter && (
                  <span className="text-sm font-normal text-slate-500 ml-2">
                    ({getFilteredKpis().length} de {dashboardData?.kpis?.length || 0})
                  </span>
                )}
              </CardTitle>
              
              <div className="flex items-center gap-2">
                {/* Active Filter Badge */}
                {statusFilter && (
                  <Badge 
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1 border",
                      getStatusFilterBadgeClass(statusFilter)
                    )}
                    data-testid="active-filter-badge"
                  >
                    <Filter className="w-3 h-3" />
                    {getFilterLabel(statusFilter)}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFilter();
                      }}
                      className="ml-1 hover:bg-black/10 rounded-full p-0.5"
                      data-testid="clear-filter-btn"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {getFilteredKpis().length === 0 ? (
              <div className="text-center py-8 sm:py-12 px-4">
                <Target className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-3" />
                {statusFilter ? (
                  <>
                    <p className="text-slate-500 text-sm sm:text-base">
                      No hay KPIs con estado &ldquo;{getFilterLabel(statusFilter)}&rdquo;
                    </p>
                    <Button 
                      variant="outline" 
                      className="mt-4" 
                      onClick={clearFilter}
                      data-testid="clear-filter-empty-btn"
                    >
                      Limpiar filtro
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-slate-500 text-sm sm:text-base">No hay KPIs configurados</p>
                    {isAdmin() && (
                      <Button variant="outline" className="mt-4" onClick={() => navigate('/kpis')}>
                        Configurar KPIs
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {getFilteredKpis().map((kpi) => (
                  <KPIRow
                    key={kpi.id}
                    kpi={kpi}
                    onNavigate={navigate}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;
